/**
 * Lo que un asesor miraría (puro): gastos raros frente a lo normal (con su nota o su motivo), qué cargos fijos
 * faltan por pasar en cada cuenta y si hay saldo, colchón de emergencia, cambios de cuota y el ahorro en contexto.
 */
import { monthTotals, prevMonth, recurringCharges, chargeChange, round2, SAVINGS_GOAL, type RecurringCharge } from '@pf/domain';
import { accountLines, type Computed } from './model.js';
import { plannedIndex, plannedInMonth } from './planned.js';

const lastMonths = (month: string, n: number) => { const out: string[] = []; let m = month; for (let i = 0; i < n; i++) { m = prevMonth(m); out.push(m); } return out; };
/** Meses cerrados anteriores con datos (máximo 12): la base de "lo normal". */
const history = (c: Computed, month: string) => { const first = c.months[c.months.length - 1] ?? month; return lastMonths(month, 12).filter(m => m >= first); };

export interface CategoryInsight {
  category: string; amount: number; normal: number | null; ratio: number | null; unusual: boolean;
  note: string; motive: { concept: string; amount: number } | null;
  /** Pagado de gastos previstos este mes (vinculado a su movimiento) y sus conceptos. No cuenta como raro. */
  planned: number; plannedConcepts: string[];
}

/** Gastos del mes por categoría (en positivo), con lo normal = media de los 12 meses cerrados anteriores con datos. */
export function categoryInsights(c: Computed, month: string): CategoryInsight[] {
  const hist = history(c, month);
  const byCatMonth = new Map<string, number>();
  const first = new Map<string, string>();
  for (const it of c.items) {
    if (it.kind !== 'expense' || !it.month) continue;
    byCatMonth.set(`${it.category}|${it.month}`, (byCatMonth.get(`${it.category}|${it.month}`) ?? 0) - it.attributable);
    if (!first.has(it.category) || it.month < first.get(it.category)!) first.set(it.category, it.month);
  }
  const cats = new Set(c.items.filter(i => i.kind === 'expense' && i.month === month).map(i => i.category));
  return [...cats].map(category => {
    const amount = round2(byCatMonth.get(`${category}|${month}`) ?? 0);
    // Lo normal se cuenta desde que existe el gasto (un gasto que empezó en mayo no tiene "0 €" de enero a abril)
    const window = hist.filter(m => m >= (first.get(category) ?? month));
    const normal = window.length >= 3 ? round2(window.reduce((t, m) => t + (byCatMonth.get(`${category}|${m}`) ?? 0), 0) / window.length) : null;
    // Raro: el doble de lo normal y al menos 100 € más (para no avisar de 30 € frente a 12 €), sin contar lo que es
    // el pago de un gasto previsto (vinculado a su movimiento)
    const pl = plannedInMonth(c, category, month);
    const unplanned = round2(amount - pl.amount);
    const unusual = normal !== null && unplanned >= 2 * normal && unplanned - normal >= 100;
    const note = c.wb.notes.find(n => n.month === month && n.category.toLowerCase() === category.toLowerCase())?.note ?? '';
    const planIds = plannedIndex(c).byMovement;
    const big = c.movements.filter(m => m.effMonth === month && m.effCategory === category && m.attributable < 0 && !planIds.has(m.id)).sort((a, b) => a.attributable - b.attributable)[0];
    return { category, amount, normal, ratio: normal ? Math.round(amount / normal * 10) / 10 : null, unusual, note,
      motive: big ? { concept: big.concept.split(' - ')[0]!.trim(), amount: round2(-big.attributable) } : null, planned: pl.amount, plannedConcepts: pl.concepts };
  }).filter(x => Math.abs(x.amount) >= 0.005).sort((a, b) => b.amount - a.amount);
}

/** Lo gastado en una categoría mes a mes (12 meses hasta `month`), con lo normal de cada mes y su nota. */
export function categoryHistory(c: Computed, category: string, month: string) {
  const months = [month, ...lastMonths(month, 11)].reverse();
  return months.map(m => {
    const ins = categoryInsights(c, m).find(x => x.category === category);
    const amount = round2(-c.items.filter(i => i.month === m && i.category === category && i.kind === 'expense').reduce((t, i) => t + i.attributable, 0));
    return { month: m, amount, normal: ins?.normal ?? null, unusual: !!ins?.unusual, planned: ins?.planned ?? 0, plannedConcepts: ins?.plannedConcepts ?? [],
      note: c.wb.notes.find(n => n.month === m && n.category.toLowerCase() === category.toLowerCase())?.note ?? '' };
  }).filter(x => x.month <= month);
}

export interface PendingCharge { label: string; category: string; day: number; amount: number; fromBudget: boolean; /** tu parte del cargo (0,5 si es compartido) */ share: number }
export interface AccountCoverage { account: string; balance: number; pending: PendingCharge[]; pendingTotal: number; covered: boolean; budgetHint: string }

/**
 * Cargos fijos que faltan por pasar este mes en cada cuenta y si el saldo los cubre. Por categoría: si tiene
 * presupuesto ese mes se usa (lo que queda de él, en importe del banco); si no, lo que se cobró la última vez.
 */
export function accountCoverage(c: Computed, month: string): AccountCoverage[] {
  // Recibos que faltan por pasar: los de categorías de naturaleza Fijo y los que son "un cargo al mes de importe
  // estable" (suscripciones, cuotas en categorías No cuenta como la hipoteca conjunta). Las compras con tarjeta
  // (varias al mes, importe variable) nunca, aunque se repitan cada mes en el mismo comercio.
  const fixed = new Set(c.wb.categories.filter(x => x.nature === 'fixed' && x.kind !== 'income').map(x => x.name.toLowerCase()));
  const variable = new Set(c.wb.categories.filter(x => x.nature === 'variable').map(x => x.name.toLowerCase()));
  const bill = (r: RecurringCharge) => r.maxPerMonth === 1 && (fixed.has(r.category.toLowerCase()) || (stableAmount(r) && !variable.has(r.category.toLowerCase())) || (stableAmount(r) && r.months >= 3));
  // Si en esa cuenta y categoría ya se ha cobrado este mes lo de siempre, no queda nada pendiente (el concepto del
  // banco puede variar un poco de un mes a otro y no casar con el grupo)
  const paidNow = (account: string, cat: string) => c.movements.filter(m => m.account === account && m.effCategory === cat && m.amount < 0 && m.date.slice(0, 7) === month)
    .reduce((t, m) => t + Math.abs(m.amount), 0);
  const all = recurringOf(c, month).filter(bill);
  const charges = all.filter(r => {
    const same = all.filter(o => o.account === r.account && o.category === r.category);
    return paidNow(r.account, r.category) < 0.9 * same.reduce((t, o) => t + Math.abs(o.last), 0);
  });
  const year = Number(month.slice(0, 4)), mi = Number(month.slice(5, 7)) - 1;
  const planned = (cat: string) => { const b = c.budget.find(x => x.year === year && x.category.toLowerCase() === cat.toLowerCase()); const v = b ? (b.overrides[mi] ?? b.monthly) : 0; return v > 0 ? v : null; };
  const spent = (cat: string) => -c.items.filter(i => i.month === month && i.category.toLowerCase() === cat.toLowerCase()).reduce((t, i) => t + i.attributable, 0);
  const pendingOf = new Map<string, PendingCharge[]>();
  const hints = new Map<string, string>();
  for (const cat of new Set(charges.map(r => r.category))) {
    const group = charges.filter(r => r.category === cat);
    const waiting = group.filter(r => !r.chargedThisMonth);
    if (!waiting.length) continue;
    const share = group.reduce((t, r) => t + r.share * Math.abs(r.last), 0) / Math.max(0.01, group.reduce((t, r) => t + Math.abs(r.last), 0)) || 1;
    // El presupuesto (tu parte) solo vale para el cargo del banco si se le parece: con reembolsos (p. ej. Bizums
    // de una suscripción compartida) el presupuesto es tu coste neto, no lo que cobrará el banco
    const usual = round2(group.reduce((t, r) => t + Math.abs(r.last) * r.share, 0));
    const p0 = planned(cat);
    const plan = p0 !== null && p0 >= usual * 0.5 && p0 <= usual * 2 ? p0 : null;
    // Presupuesto (en tu parte) → importe del banco; se reparte entre los cargos pendientes según su última cuota
    const left = plan === null ? null : Math.max(0, plan - spent(cat)) / share;
    const base = waiting.reduce((t, r) => t + Math.abs(r.last), 0);
    for (const r of waiting) {
      const amount = round2(left === null ? Math.abs(r.last) : left * (Math.abs(r.last) / (base || 1)));
      pendingOf.set(r.account, [...(pendingOf.get(r.account) ?? []), { label: r.label, category: cat, day: r.day, amount, fromBudget: left !== null, share: r.share }]);
    }
    if (plan !== null) {
      if (Math.abs(usual - plan) > Math.max(5, plan * 0.03)) hints.set(group[0]!.account, `Tu presupuesto de ${cat} (${plan.toFixed(2).replace('.', ',')} €) no coincide con lo que se cobra (${usual.toFixed(2).replace('.', ',')} €)`);
    }
  }
  return accountLines(c).map(a => {
    const pending = (pendingOf.get(a.name) ?? []).sort((x, y) => x.day - y.day);
    const pendingTotal = round2(pending.reduce((t, p) => t + p.amount, 0));
    return { account: a.name, balance: a.recorded, pending, pendingTotal, covered: a.recorded >= pendingTotal, budgetHint: hints.get(a.name) ?? '' };
  });
}

/** Importe estable: al menos dos de los tres últimos cargos iguales (±1 %). */
export function stableAmount(r: RecurringCharge): boolean {
  const h = r.history.slice(-3).map(Math.abs);
  return h.some((a, i) => h.some((b, j) => i < j && Math.abs(a - b) <= Math.max(0.5, a * 0.01)));
}

let cache: { c: Computed; month: string; out: RecurringCharge[] } | null = null;
/** Cargos recurrentes (con caché por cálculo: Inicio los pide varias veces). */
export function recurringOf(c: Computed, month: string): RecurringCharge[] {
  if (cache && cache.c === c && cache.month === month) return cache.out;
  const out = recurringCharges(c.movements.map(m => ({ id: m.id, date: m.date, concept: m.concept, amount: m.amount, attributable: m.attributable, account: m.account, category: m.effCategory })), month);
  cache = { c, month, out };
  return out;
}

/** Cuotas que han cambiado en el último cargo (de los dos últimos meses). */
export function chargeChanges(c: Computed, month: string) {
  const recent = new Set([month, prevMonth(month)]);
  return recurringOf(c, month).filter(r => recent.has(r.lastMonth)).flatMap(r => { const ch = chargeChange(r); return ch ? [{ ...ch, label: r.label, account: r.account }] : []; });
}

/** Colchón: liquidez de hoy / gasto medio mensual de los 12 meses cerrados anteriores. */
export function emergencyMonths(c: Computed, month: string, liquidity: number): number | null {
  const avg = monthlyExpenseAvg(c, month);
  return avg ? Math.round(liquidity / avg * 10) / 10 : null;
}

/** Gasto medio al mes (en positivo) de los 12 meses cerrados anteriores con datos; null con menos de 3 meses. */
export function monthlyExpenseAvg(c: Computed, month: string): number | null {
  const hist = history(c, month);
  if (hist.length < 3) return null;
  const avg = Math.abs(hist.reduce((t, m) => t + monthTotals(c.items, m).expenses, 0) / hist.length);   // los gastos van en negativo
  return avg > 0 ? round2(avg) : null;
}

/** El ahorro del mes en contexto: media de 12 meses, objetivo y gastos fijos que faltan por pasar (tu parte). */
export function savingsContext(c: Computed, month: string, coverage: AccountCoverage[]) {
  const hist = history(c, month);
  const avg = hist.length ? round2(hist.reduce((t, m) => t + monthTotals(c.items, m).savings, 0) / hist.length) : null;
  const year = Number(month.slice(0, 4)), mi = Number(month.slice(5, 7)) - 1;
  const g = c.wb.budget.find(b => b.year === year && b.category === SAVINGS_GOAL);
  const goal = g ? (g.overrides[mi] ?? g.monthly) : null;
  // Solo lo que cuenta en tu ahorro (lo No cuenta, como la hipoteca conjunta, tiene parte 0)
  const pending = coverage.flatMap(a => a.pending.map(p => ({ ...p, mine: round2(p.amount * p.share) }))).filter(p => p.mine > 0);
  return { avg, goal: goal || null, pending, pendingMine: round2(pending.reduce((t, p) => t + p.mine, 0)) };
}
